import React, { FC, useCallback, useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import { useFormik } from 'formik';
import AuthContext from '../../../contexts/authContext';

import {
	getAuth,
	signInWithEmailAndPassword,
	sendPasswordResetEmail,
	createUserWithEmailAndPassword,
	updateProfile,
} from 'firebase/auth';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '../../../components/bootstrap/Modal';

interface ILoginHeaderProps {
	isNewUser?: boolean;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ isNewUser }) => {
	if (isNewUser) {
		return (
			<>
				<div className='text-center h1 fw-bold mt-5'>새롭게 가입하기</div>
				<div className='text-center h4 text-muted mb-5'>관리자에게 코드를 문의하세요</div>
			</>
		);
	}
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>환영합니다,</div>
			<div className='text-center h4 text-muted mb-5'>앎삶쉼 공간 예약시스템입니다.</div>
		</>
	);
};

interface ILoginProps {
	isSignUp?: boolean;
}
const Login: FC<ILoginProps> = ({ isSignUp }) => {
	const { setUser } = useContext(AuthContext);
	const auth = getAuth();

	const { darkModeStatus } = useDarkMode();

	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);
	const [passwordErrorCount, setPasswordErrorCount] = useState<number>(0);
	const [modal, setModal] = useState<boolean>(false);

	const navigate = useNavigate();
	const handleOnClick = useCallback(() => navigate('/dashboard'), [navigate]);
	const handleOnClick2 = useCallback(() => navigate('/'), [navigate]);

	const usernameCheck = (username: string) => {
		const regex =
			/^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/;
		return regex.test(username) && username.length > 6;
	};

	const passwordCheck = (pw: string) => {
		const regexPw = /^[A-Za-z0-9`~!@#\$%\^&\*\(\)\{\}\[\]\-_=\+\\|;:'"<>,\./\?]{8,20}$/;
		return regexPw.test(pw);
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginUsername: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: {
				loginUsername?: string;
				loginPassword?: string;
			} = {};

			if (!values.loginUsername) {
				errors.loginUsername = '이메일을 입력하세요';
			}

			if (!usernameCheck(values.loginUsername)) {
				errors.loginUsername = '올바른 이메일 주소를 입력하세요';
			}

			if (!values.loginPassword) {
				errors.loginPassword = '비밀번호를 입력하세요';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			signInWithEmailAndPassword(auth, values.loginUsername, values.loginPassword)
				.then(async (userCredential) => {
					if (setUser) {
						setUser(userCredential);
					}
					handleOnClick();
				})
				.catch((error) => {
					const errorCode = error.code;
					const errorMessage = error.message;
					console.log(errorCode);
					if (errorCode === 'auth/wrong-password') {
						formik.setFieldError('loginPassword', '이메일 또는 비밀번호를 확인하세요');
						setPasswordErrorCount((prev) => prev + 1);
					} else if (errorCode === 'auth/user-not-found') {
						formik.setFieldError('loginPassword', '등록되지 않은 이메일입니다');
					} else if (errorCode === 'auth/too-many-requests') {
						formik.setFieldError(
							'loginPassword',
							'일시적인 오류로 로그인을 할 수 없습니다. 잠시 후 다시 이용해 주세요.',
						);
					} else {
						formik.setFieldError(
							'loginPassword',
							'로그인을 할 수 없습니다. 잠시 후 다시 이용해 주세요.',
						);
						console.log(errorCode);
					}
					if (passwordErrorCount > 2) {
						sendPasswordResetEmail(auth, values.loginUsername);
						setModal(true);
					}
				});
		},
	});

	const formik2 = useFormik({
		enableReinitialize: true,
		initialValues: {
			signUpEmail: '',
			signUpPassword: '',
			signUpPasswordConfirm: '',
			signUpName: '',
			signUpPosition: '',
			signUpCode: '',
		},
		validate: (values) => {
			const errors: {
				signUpEmail?: string;
				signUpPassword?: string;
				signUpPasswordConfirm?: string;
				signUpName?: string;
				signUpPosition?: string;
				signUpCode?: string;
			} = {};

			if (!values.signUpEmail) {
				errors.signUpEmail = '이메일을 입력하세요';
			}

			if (!usernameCheck(values.signUpEmail)) {
				errors.signUpEmail = '올바른 이메일 주소를 입력하세요';
			}

			if (!values.signUpPassword) {
				errors.signUpPassword = '비밀번호를 입력하세요';
			}

			if (!passwordCheck(values.signUpPassword)) {
				errors.signUpPassword = '8~20자 영문 대소문자, 숫자, 특수문자를 사용하세요';
			}

			if (!values.signUpPasswordConfirm) {
				errors.signUpPasswordConfirm = '비밀번호를 다시 입력하세요';
			}
			if (values.signUpPassword !== values.signUpPasswordConfirm) {
				errors.signUpPasswordConfirm = '비밀번호가 일치하지 않습니다';
			}
			if (!values.signUpName) {
				errors.signUpName = '이름을 입력하세요';
			}
			if (!values.signUpPosition) {
				errors.signUpPosition = '선생님 또는 학생을 입력해주세요';
			}
			if (!values.signUpCode) {
				errors.signUpCode = '코드를 입력하세요';
			}

			if (values.signUpCode.trim() !== 'daeji1234') {
				errors.signUpCode = '코드가 일치하지 않습니다 다시 확인해주세요';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			createUserWithEmailAndPassword(auth, values.signUpEmail, values.signUpPassword)
				.then(async (userCredentials) => {
					await updateProfile(userCredentials.user, {
						displayName: values.signUpName + ' ' + values.signUpPosition,
					});
					if (setUser) {
						setUser(userCredentials);
						handleOnClick();
					}
				})
				.catch((error) => {
					const errorCode = error.code;
					const errorMessage = error.message;
					console.log(errorCode);
					if (errorCode === 'auth/email-already-in-use') {
						formik2.setFieldError('signUpEmail', '이미 가입된 이메일 주소입니다.');
					} else {
						formik2.setFieldError(
							'signUpEmail',
							'가입중 오류가 발생했습니다 다시 시도해주세요',
						);
					}
				});
		},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);

	return (
		<PageWrapper
			isProtected={false}
			title={singUpStatus ? '가입하기' : '로그인'}
			className={classNames({ 'bg-warning': !singUpStatus, 'bg-info': singUpStatus })}>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-center my-5'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}>
										<Logo width={220} height={200} />
									</Link>
								</div>
								<div
									className={classNames('rounded-3', {
										'bg-l10-dark': !darkModeStatus,
										'bg-dark': darkModeStatus,
									})}>
									<div className='row row-cols-2 g-3 pb-3 px-3 mt-0'>
										<div className='col'>
											<Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={singUpStatus}
												className='rounded-1 w-100'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												로그인
											</Button>
										</div>
										<div className='col'>
											<Button
												color={darkModeStatus ? 'light' : 'dark'}
												isLight={!singUpStatus}
												className='rounded-1 w-100'
												size='lg'
												onClick={() => {
													setSignInPassword(false);
													setSingUpStatus(!singUpStatus);
												}}>
												가입하기
											</Button>
										</div>
									</div>
								</div>

								<LoginHeader isNewUser={singUpStatus} />

								<form className='row g-4'>
									{singUpStatus ? (
										<>
											<div className='col-12'>
												<FormGroup
													id='signUpEmail'
													isFloating
													label='이메일 주소'>
													<Input
														type='email'
														autoComplete='email'
														value={formik2.values.signUpEmail}
														isTouched={formik2.touched.signUpEmail}
														invalidFeedback={formik2.errors.signUpEmail}
														isValid={formik2.isValid}
														onChange={formik2.handleChange}
														onBlur={formik2.handleBlur}
														onFocus={() => {
															formik2.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='signUpPassword'
													isFloating
													label='비밀번호'>
													<Input
														type='password'
														autoComplete='password'
														value={formik2.values.signUpPassword}
														isTouched={formik2.touched.signUpPassword}
														invalidFeedback={
															formik2.errors.signUpPassword
														}
														isValid={formik2.isValid}
														onChange={formik2.handleChange}
														onBlur={formik2.handleBlur}
														onFocus={() => {
															formik2.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='signUpPasswordConfirm'
													isFloating
													label='비밀번호확인'>
													<Input
														type='password'
														autoComplete='password'
														value={formik2.values.signUpPasswordConfirm}
														isTouched={
															formik2.touched.signUpPasswordConfirm
														}
														invalidFeedback={
															formik2.errors.signUpPasswordConfirm
														}
														isValid={formik2.isValid}
														onChange={formik2.handleChange}
														onBlur={formik2.handleBlur}
														onFocus={() => {
															formik2.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup id='signUpName' isFloating label='이름'>
													<Input
														autoComplete='given-name'
														value={formik2.values.signUpName}
														isTouched={formik2.touched.signUpName}
														invalidFeedback={formik2.errors.signUpName}
														isValid={formik2.isValid}
														onChange={formik2.handleChange}
														onBlur={formik2.handleBlur}
														onFocus={() => {
															formik2.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='signUpPosition'
													isFloating
													label='호칭  예) 선생님 학생'>
													<Input
														autoComplete='none'
														value={formik2.values.signUpPosition}
														isTouched={formik2.touched.signUpPosition}
														invalidFeedback={
															formik2.errors.signUpPosition
														}
														isValid={formik2.isValid}
														onChange={formik2.handleChange}
														onBlur={formik2.handleBlur}
														onFocus={() => {
															formik2.setErrors({});
														}}
													/>
												</FormGroup>
											</div>

											<div className='col-12'>
												<FormGroup
													id='signUpCode'
													isFloating
													label='관리자승인코드'>
													<Input
														type='text'
														autoComplete='none'
														value={formik2.values.signUpCode}
														isTouched={formik2.touched.signUpCode}
														invalidFeedback={formik2.errors.signUpCode}
														isValid={formik2.isValid}
														onChange={formik2.handleChange}
														onBlur={formik2.handleBlur}
														onFocus={() => {
															formik2.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													color='info'
													className='w-100 py-3'
													onClick={formik2.handleSubmit}>
													가입하기
												</Button>
											</div>
										</>
									) : (
										<>
											<div className='col-12'>
												<FormGroup
													id='loginUsername'
													isFloating
													label='이메일주소'>
													<Input
														autoComplete='email'
														value={formik.values.loginUsername}
														isTouched={formik.touched.loginUsername}
														invalidFeedback={
															formik.errors.loginUsername
														}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														onFocus={() => {
															formik.setErrors({});
														}}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<FormGroup
													id='loginPassword'
													isFloating
													label='비밀번호'>
													<Input
														type='password'
														autoComplete='current-password'
														value={formik.values.loginPassword}
														isTouched={formik.touched.loginPassword}
														invalidFeedback={
															formik.errors.loginPassword
														}
														isValid={formik.isValid}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
													/>
												</FormGroup>
											</div>
											<div className='col-12'>
												<Button
													color='warning'
													className='w-100 py-3'
													onClick={formik.handleSubmit}>
													로그인
												</Button>
											</div>
										</>
									)}
								</form>
							</CardBody>
						</Card>
						<div className='text-center'>
							<a
								href='http://www.daeji.ms.kr/'
								className={classNames('text-decoration-none me-3', {
									'link-light': singUpStatus,
									'link-dark': !singUpStatus,
								})}>
								© 2021 대지중학교
							</a>
						</div>
					</div>
				</div>
			</Page>
			<Modal
				id='alert'
				isOpen={modal}
				setIsOpen={setModal}
				isStaticBackdrop={true}
				isScrollable={false}
				isCentered={true}
				fullScreen={'lg'} // true || 'sm' || 'md' || 'lg' || 'xl' || 'xxl'
				isAnimation={true}>
				<ModalHeader>
					<ModalTitle id='alert'>비밀번호 3회 오류</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{`가입하신 메일주소로 비밀번호 재설정 이메일이 발송되었습니다. 이메일 확인하여 비밀번호를 재설정 후
					다시 로그인해주세요`}
				</ModalBody>
				<ModalFooter>
					<Button
						color='info'
						onClick={() => {
							setModal(false);
							setPasswordErrorCount(0);
						}}>
						확인
					</Button>
				</ModalFooter>
			</Modal>
		</PageWrapper>
	);
};
Login.defaultProps = {
	isSignUp: false,
};

export default Login;
